import { TimePicker } from '@systemeio/components';
import { ArrowNarrowLeftIcon } from '@systemeio/icons';
import { BadRequest } from '@systemeio/util';
import { updateFunnelEmail } from 'modules/funnels/funnel/components/email-tab/api/funnel-email-api';
import { EventTypeEnum } from 'modules/funnels/funnel/components/email-tab/enums/funnel-emails-enum';
import { useFunnelEmail } from 'modules/funnels/funnel/components/email-tab/hooks/use-funnel-emails';
import { FunnelEmailsErrorInterface } from 'modules/funnels/funnel/components/email-tab/types/funnel-emails-error-itnerface';
import { FunnelEmailEditInterface, FunnelEmailsApiInterface, FunnelEmailsInterface, FunnelEmailViewInterface } from 'modules/funnels/funnel/components/email-tab/types/funnel-emails-interface';
import { FunnelStepId } from 'modules/funnels/funnel/types/funnel-step-interface';
import { useCallback, useEffect, useState } from 'react';
import { Button, FormSelect, toast } from 'shared/components';
import DashboardMailEditor from 'shared/components/dashboard-mail-editor';
import { useUnsavedEmail } from 'shared/components/dashboard-mail-editor/hooks/use-unsaved-email';
import { AttachmentFileInterface, EmailBaseInterface, EmailErrorType, UnsavedAttachmentFileInterface } from 'shared/components/dashboard-mail-editor/types/email-interface';
import { getMailingUpdateData } from 'shared/components/dashboard-mail-editor/utils/get-mailing-update-data';
import TimeDelayPicker, { TimeDelayPickerProps } from 'shared/components/date-picker/time-delay-picker';
import { TimeType, TimeTypeState } from 'shared/components/date-picker/time-delay-picker/constants';
import { convertSecondsToTimeValue, convertTimeValueToSeconds } from 'shared/components/date-picker/time-delay-picker/utils';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { isValidEmail } from 'shared/utils/email-validation';
import { KeyedMutator } from 'swr';
const defaultErrors: EmailErrorType = {
  body: '',
  fromEmail: '',
  fromName: '',
  subject: '',
  previewText: '',
  common: [],
  delay: '',
  attachments: ''
};
interface FunnelEmailUpdateProps {
  onBack: () => void;
  instance: FunnelEmailsInterface;
  funnelStepId?: FunnelStepId;
  mutateEmails: KeyedMutator<FunnelEmailsApiInterface>;
}
const FunnelEmailUpdate = ({
  onBack,
  instance,
  funnelStepId,
  mutateEmails
}: FunnelEmailUpdateProps) => {
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const [isFetching, setIsFetching] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<EventTypeEnum>(EventTypeEnum.registration_to_webinar);
  const [chosenTimeDelay, setChosenTimeDelay] = useState<number>(1);
  const [chosenSendingEmailTime, setChosenSendingEmailTime] = useState<number | null>(null);
  const [selectedTimeBeforeAfter, setSelectedTimeBeforeAfter] = useState<TimeTypeState | undefined>(TimeTypeState.after);
  const [selectedTimeType, setSelectedTimeType] = useState<TimeType>(TimeType.MINUTES);
  const [tempState, setTempState] = useState<EmailBaseInterface>();
  const [errors, setErrors] = useState<EmailErrorType>(defaultErrors);
  const [attachmentsTempState, setAttachmentsTempState] = useState<(AttachmentFileInterface | UnsavedAttachmentFileInterface)[]>([]);
  const {
    emailData,
    mutateEmailData
  } = useFunnelEmail(funnelStepId, instance.webinarNotification.id, true);
  const eventSelector = [{
    caption: t('dashboard.funnel.emails_registration_to_webinar'),
    id: EventTypeEnum.registration_to_webinar
  }, {
    caption: t('dashboard.funnel.emails_webinar_start'),
    id: EventTypeEnum.webinar_start
  }];
  const applyEmailData = useCallback((emailData_0: FunnelEmailViewInterface | undefined) => {
    if (!emailData_0) return;
    const delay = convertSecondsToTimeValue(emailData_0.delay);
    setTempState(prev => ({
      ...prev,
      body: emailData_0.mailing.currentEmail.body,
      subject: emailData_0.mailing.currentEmail.subject,
      previewText: emailData_0.mailing.currentEmail.previewText,
      fromEmail: emailData_0.mailing.currentEmail.fromEmail,
      fromName: emailData_0.mailing.currentEmail.fromName
    }));
    if (delay) {
      setSelectedTimeBeforeAfter(delay.beforeAfter);
      setSelectedTimeType(delay.timeType);
      setChosenTimeDelay(delay.timeValue);
    }
    if (emailData_0.mailing.currentEmail.attachments) {
      setAttachmentsTempState(emailData_0.mailing.currentEmail.attachments);
    }
    setSelectedEventType(emailData_0.event);
    setChosenSendingEmailTime(emailData_0.certainTime);
  }, []);
  useEffect(() => {
    applyEmailData(emailData);
  }, [applyEmailData, emailData]);
  const onUpdate = async () => {
    if (!emailData) return;
    try {
      if (tempState && funnelStepId) {
        setErrors(defaultErrors);
        if (!isValidEmail(tempState.fromEmail)) {
          return setErrors(prev_2 => ({
            ...prev_2,
            fromEmail: t('dashboard.validation.email_not_valid')
          }));
        }
        setIsFetching(true);
        const newData: FunnelEmailEditInterface = {
          mailing: {
            currentEmail: getMailingUpdateData({
              ...tempState,
              body: tempState.body
            }, attachmentsTempState)
          },
          event: selectedEventType,
          delay: convertTimeValueToSeconds(chosenTimeDelay, selectedTimeType, selectedTimeBeforeAfter),
          certainTime: chosenSendingEmailTime
        };
        const emailResponse = await updateFunnelEmail(funnelStepId, instance.webinarNotification.id, newData);
        if (emailResponse.data) {
          const updatedData: FunnelEmailViewInterface = {
            ...newData,
            mailing: {
              id: emailData.mailing.id,
              currentEmail: {
                id: emailData?.mailing.currentEmail.id,
                ...tempState,
                body: tempState.body,
                attachments: emailResponse.data.mailing.currentEmail.attachments
              }
            }
          };
          await mutateEmailData(data => {
            if (data) {
              return {
                ...data,
                ...updatedData
              };
            }
          }, false);
          await mutateEmails(data_0 => data_0 && {
            ...data_0,
            items: data_0.items.map(el => {
              if (el.webinarNotification.mailing.id === emailResponse.data.mailing.id) {
                el.webinarNotification.mailing.currentEmail.subject = emailResponse.data.mailing.currentEmail.subject;
              }
              return el;
            })
          }, false);
        }
        await removeDbRecord();
        toast.success(t('global.changes_saved'));
        setIsFetching(false);
        onBack();
      }
    } catch (e) {
      setIsFetching(false);
      if (e instanceof BadRequest) {
        const funnelEmailSettingsErrors = e.errors as unknown as FunnelEmailsErrorInterface;
        if (funnelEmailSettingsErrors) {
          const fieldErrors = !Array.isArray(funnelEmailSettingsErrors.fields) ? funnelEmailSettingsErrors.fields : undefined;
          if (fieldErrors) {
            setErrors(prev_0 => ({
              ...prev_0,
              body: fieldErrors.mailing?.currentEmail?.body?.join(''),
              fromEmail: fieldErrors.mailing?.currentEmail?.fromEmail?.join(''),
              fromName: fieldErrors.mailing?.currentEmail?.fromName?.join(''),
              subject: fieldErrors.mailing?.currentEmail?.subject?.join(''),
              previewText: fieldErrors.mailing?.currentEmail?.previewText?.join(''),
              delay: fieldErrors?.delay?.join('')
            }));
          }
        }
        if (e.errors.common) {
          const commonErrors = e.errors.common;
          setErrors(prev_1 => {
            return {
              ...prev_1,
              common: commonErrors
            };
          });
        }
      } else {
        toast.error(t('global.error'));
      }
    }
  };
  const renderTime = () => {
    return <div className={'flex flex-col gap-5'}>
        <FormSelect withoutCloseIcon label={t('global.event_caption')} data={eventSelector} value={selectedEventType} onChange={data_1 => {
        if (data_1 !== selectedEventType) {
          setSelectedTimeBeforeAfter(TimeTypeState.after);
          setChosenSendingEmailTime(null);
          setChosenTimeDelay(1);
        }
        if (data_1) {
          setSelectedEventType(data_1);
        }
      }} isPreFetching={!emailData} />
        <TimeDelayPicker required selectedTimeType={selectedTimeType} selectedTimeBeforeAfter={selectedTimeBeforeAfter} selectedTimeValue={chosenTimeDelay} label={t('dashboard.funnel.emails_webinar.delay')} onChange={onChooseTimeDelay} error={errors.delay} withTimeAfterBeforeSelect={selectedEventType === EventTypeEnum.webinar_start} isPreFetching={!emailData} classNameInputInteger="w-auto flex-1" />
        {(selectedEventType === EventTypeEnum.registration_to_webinar || selectedTimeBeforeAfter === TimeTypeState.after) && <TimePicker label={t('dashboard.funnel.emails_webinar.send_at_certain_time')} seconds={chosenSendingEmailTime} onChangeSeconds={setChosenSendingEmailTime} isPreFetching={!emailData} userTimeFormat={user?.timeFormat} containerClassName="w-full" wrapperClassName="w-full" inputClassName="w-full" />}
      </div>;
  };
  const onChooseTimeDelay = useCallback<TimeDelayPickerProps['onChange']>((timeDelay, timeType, timeBeforeAfter) => {
    setChosenTimeDelay(timeDelay);
    setSelectedTimeType(timeType);
    setSelectedTimeBeforeAfter(timeBeforeAfter);
    if (timeBeforeAfter !== selectedTimeBeforeAfter) {
      setChosenSendingEmailTime(null);
    }
  }, [selectedTimeBeforeAfter]);
  const onChangeEmail = (value: EmailBaseInterface) => setTempState(value);
  const {
    UnsavedEmailConfirmationModal,
    removeDbRecord
  } = useUnsavedEmail({
    currentBody: tempState?.body,
    initialBody: emailData?.mailing.currentEmail.body,
    mailingId: emailData?.mailing.id,
    onPaste: data_2 => setTempState(prev_3 => prev_3 && {
      ...prev_3,
      body: data_2
    }),
    showImmediately: true
  });
  return <div className="flex flex-col gap-5">
      <Button className="self-start" onClick={onBack} variant="ghost-gray" size="sm">
        <ArrowNarrowLeftIcon />
        {t('global.back')}
      </Button>
      <form className="desktop:gap-7 flex flex-col gap-6" onSubmit={event => {
      event.preventDefault();
      void onUpdate();
    }}>
        <DashboardMailEditor sidebarTabs={[{
        title: t('global.settings'),
        Component: renderTime(),
        showErrorOutline: !!errors.delay,
        value: 'settings'
      }]} isPreFetching={!emailData} tempState={tempState} onChangeEmail={onChangeEmail} attachmentsTempState={attachmentsTempState} setAttachmentsTempState={setAttachmentsTempState} errors={errors} additionalSubstitutes={[{
        name: t('dashboard.funnel.emails_edit.send.email_webinar.show.link'),
        slug: 'webinar_show_link'
      }, {
        name: t('dashboard.funnel.emails_edit.send.email_webinar.replay.link'),
        slug: 'webinar_replay_link'
      }]} />
        <div className="tablet:[&>*]:flex-none tablet:[&>*]:w-auto flex justify-end gap-3 [&>*]:flex-1">
          <Button onClick={onBack} type="button" variant="secondary" size="md">
            {t('confirmation_modal.cancel')}
          </Button>
          <Button disabled={isFetching} isLoading={isFetching} type="submit" variant="primary" size="md">
            {t('global.save')}
          </Button>
        </div>
      </form>
      {UnsavedEmailConfirmationModal}
    </div>;
};
export default FunnelEmailUpdate;